// Canonical icon vocabulary + the alias layer that maps the legacy
// `/icons/<name>.webp` filenames (and a few semantic aliases) onto a canonical
// IconName, optionally with a default color.
//
// This file is PACK-INDEPENDENT: it only declares WHICH icon a name means and
// its default tint. The actual glyph artwork for each canonical name is supplied
// per age-group by a pack (see ./packs/*). The 14–18 (learner) pack draws from
// Phosphor; a 10–14 pack can later map the SAME canonical names to a different,
// more playful set without touching this file or any call site.

// Canonical names — the shared semantic vocabulary every pack must cover.
export type IconName =
  // navigation (base + Fill variant used for the selected/active state)
  | "house"
  | "houseFill"
  | "folder"
  | "folderFill"
  | "chartBar"
  | "chartBarFill"
  | "megaphone"
  | "megaphoneFill"
  | "calendar"
  | "calendarFill"
  | "target"
  | "gear"
  | "signOut"
  // feature nav (homework / learning-path) — base + Fill for active state
  | "clipboardTextFill"
  | "bookOpen"
  | "bookOpenFill"
  | "path"
  | "pathFill"
  // actions
  | "download"
  | "refresh"
  | "uploadDevice"
  | "info"
  | "warning"
  | "signal"
  | "close"
  | "checkCircle"
  | "xCircle"
  | "checkboxFilled"
  | "checkboxEmpty"
  | "checkboxPartial"
  | "checkboxCross"
  | "more"
  | "fileBlank"
  | "pencil"
  | "trash"
  | "image"
  | "audio"
  | "video"
  | "text"
  | "send"
  | "userSwitch"
  // arrows / chevrons
  | "arrowUp"
  | "arrowDown"
  | "arrowLeft"
  | "arrowRight"
  | "chevronUp"
  | "chevronDown"
  | "chevronLeft"
  | "chevronRight"
  | "arrowUpRight"
  | "plus"
  // media
  | "play"
  | "pause"
  | "clock"
  // AI — the written-by-the-model marker (open-ended evaluation, AI copy)
  | "sparkle"
  // domain / org hierarchy
  | "umbrella"
  | "buildings"
  | "bank"
  | "treeStructure"
  | "graduationCap"
  | "sun"
  | "exam"
  | "clipboardText"
  | "listChecks"
  | "user"
  // status dots / stars
  | "star"
  | "starFill"
  | "circle"
  | "circleFill"
  // colored gamification / feature illustrations (Phosphor Fill, fixed color)
  | "crownFill"
  | "coin"
  | "lightning"
  | "backpack"
  | "storefront"
  | "trophy"
  | "playCircle"
  | "camera"
  | "userCircle"
  // outline (Regular) variants of the gamification glyphs — used at rest in the
  // nav so every nav item is outline-when-inactive / Fill-when-active
  | "trophyLine"
  | "storefrontLine"
  | "playCircleLine"
  | "cameraLine"
  | "userCircleLine"
  | "backpackLine"
  | "gift"
  | "translate"
  | "microphone"
  | "speakerFill"
  // empty / gate states (the <EmptyState> medallion glyphs)
  | "map"
  | "magnifyingGlass"
  | "tray"
  | "confetti"
  | "users"
  | "sword"
  | "lock"
  // support-assignment / drawing toolbar
  | "eraser"
  | "lineSegment"
  | "rectangle"
  | "scribble"
  | "cursor";

export type AliasEntry = { name: IconName; defaultColor?: string };

// Default tints (used only when the caller does not pass an explicit `color`).
export const GRAY = "#9ca3af";
export const RED = "#ef4444";
export const ORANGE = "#f59e0b";
export const GREEN = "#10b981";
export const BLUE = "#3b82f6";
export const WHITE = "#ffffff";
// Thematic fills for the colored gamification/feature illustrations (multi-color
// raster art replaced by Phosphor Fill glyphs painted a fixed identity color).
export const GOLD = "#F4B400";
export const SILVER = "#9AA7B0";
export const BRONZE = "#CD7F32";
export const BRAND = "#54BBCB";

// Legacy `.webp` filename (and semantic) → canonical name [+ default color].
// `<Icon name="dashboard-selected" />` resolves here; `<Icon name="house" />`
// (a canonical name) is also accepted directly by the engine.
export const ALIASES: Record<string, AliasEntry> = {
  // navigation (selected → Fill weight)
  ai: { name: "sparkle", defaultColor: BRAND },
  dashboard: { name: "house" },
  "dashboard-selected": { name: "houseFill" },
  category: { name: "folder" },
  "category-selected": { name: "folderFill" },
  report: { name: "chartBar" },
  "report-selected": { name: "chartBarFill" },
  announcement: { name: "megaphone" },
  "announcement-selected": { name: "megaphoneFill" },
  "framework-annual-plan": { name: "calendar" },
  "framework-annual-plan-selected": { name: "calendarFill" },
  outcome: { name: "target" },
  settings: { name: "gear" },
  logout: { name: "signOut" },
  // feature nav — semantic names whose glyph actually matches the feature
  // (homework = clipboard of tasks, learning-path = a route). The support-assignment
  // aliases were removed with the Çalışmalar nav entry; a destek ödevi is now iconed by
  // the shared kind table (AssignmentKindUtilization → canonical "users").
  homework: { name: "clipboardText" },
  "homework-selected": { name: "clipboardTextFill" },
  "learning-path": { name: "path" },
  "learning-path-selected": { name: "pathFill" },

  // arrows / chevrons
  "arrow-up": { name: "arrowUp" },
  "arrow-down": { name: "arrowDown" },
  "arrow-left": { name: "arrowLeft" },
  "arrow-right": { name: "arrowRight" },
  "long-arrow-left": { name: "arrowLeft" },
  "long-arrow-right": { name: "arrowRight" },
  "header-arrow-right": { name: "chevronRight" },
  "header-arrow-down": { name: "chevronDown" },
  "header-arrow-up-selected": { name: "chevronUp" },
  "header-arrow-left": { name: "chevronLeft" },
  "diagonal-arrow": { name: "arrowUpRight" },
  "cancel-button-arrow-left": { name: "arrowLeft" },

  // actions / status
  download: { name: "download" },
  refresh: { name: "refresh" },
  "upload-device": { name: "uploadDevice" },
  "upload-pc": { name: "uploadDevice" },
  "upload-draw": { name: "uploadDevice" },
  info: { name: "info" },
  "info-red": { name: "info", defaultColor: RED },
  "info-orange": { name: "info", defaultColor: ORANGE },
  "info-blue": { name: "info", defaultColor: BLUE },
  "exclamation-red": { name: "warning", defaultColor: RED },
  "cross-red": { name: "close", defaultColor: RED },
  "check-circle-green": { name: "checkCircle", defaultColor: GREEN },
  completed: { name: "checkCircle" },
  checkbox: { name: "checkboxFilled" },
  "checkbox-empty": { name: "checkboxEmpty" },
  "checkbox-partial": { name: "checkboxPartial" },
  "checkbox-cross": { name: "checkboxCross" },
  "three-dots": { name: "more" },
  file: { name: "fileBlank" },
  "edit-white": { name: "pencil", defaultColor: WHITE },
  "remove-red": { name: "trash", defaultColor: RED },
  "image-gray": { name: "image", defaultColor: GRAY },
  "audio-gray": { name: "audio", defaultColor: GRAY },
  "video-gray": { name: "video", defaultColor: GRAY },
  "text-gray": { name: "text", defaultColor: GRAY },
  send: { name: "send" },
  "change-profile": { name: "userSwitch" },
  "user-profile": { name: "user" },

  // status dots / stars
  "item-element": { name: "circleFill" },
  "not-selected-item-element": { name: "circle" },
  "status-item": { name: "circleFill" },
  "small-dot-gray": { name: "circleFill", defaultColor: GRAY },
  "empty-circle": { name: "circle" },
  "star-fill": { name: "starFill" },
  "star-empty": { name: "star" },

  // media
  play: { name: "play" },
  pause: { name: "pause" },
  clock: { name: "clock" },
  timer: { name: "clock" },

  // domain / org hierarchy
  holiday: { name: "umbrella" },
  institution: { name: "buildings" },
  campus: { name: "bank" },
  branch: { name: "treeStructure" },
  grade: { name: "graduationCap" },
  season: { name: "sun" },
  "standard-exam": { name: "exam" },
  "corporate-assessment-assignment": { name: "clipboardText" },
  "completion-assignment": { name: "listChecks" },
  tyt: { name: "exam" },
  ayt: { name: "exam" },
  lgs: { name: "exam" },

  // support-assignment / drawing toolbar (active state handled by `color`, same glyph)
  draw: { name: "pencil" },
  eraser: { name: "eraser" },
  line: { name: "lineSegment" },
  rectangle: { name: "rectangle" },
  circle: { name: "circle" },
  scribble: { name: "scribble" },
  select: { name: "cursor" },

  // colored gamification / feature art (former raster /icons → /images webp).
  // Phosphor Fill glyphs painted a fixed identity color via defaultColor.
  "crown-gold": { name: "crownFill", defaultColor: GOLD },
  "crown-silver": { name: "crownFill", defaultColor: SILVER },
  "crown-bronze": { name: "crownFill", defaultColor: BRONZE },
  points: { name: "coin", defaultColor: GOLD },
  speed: { name: "lightning", defaultColor: ORANGE },
  bag: { name: "backpackLine", defaultColor: BRAND },
  // base = outline (Regular), selected = Fill — so the nav rail is one weight at
  // rest and only the active item fills in (defaultColor is ignored by the nav,
  // which forces currentColor, but kept for the few non-nav callers).
  market: { name: "storefrontLine", defaultColor: BRAND },
  "market-selected": { name: "storefront", defaultColor: BRAND },
  contest: { name: "trophyLine", defaultColor: GOLD },
  "contest-selected": { name: "trophy", defaultColor: GOLD },
  "practise-now": { name: "playCircleLine", defaultColor: BRAND },
  "practise-now-selected": { name: "playCircle", defaultColor: BRAND },
  "photo-solve": { name: "cameraLine", defaultColor: BRAND },
  "photo-solve-selected": { name: "camera", defaultColor: BRAND },
  profile: { name: "userCircleLine", defaultColor: BRAND },
  "profile-selected": { name: "userCircle", defaultColor: BRAND },
  "birthday-blue": { name: "gift", defaultColor: BLUE },
  language: { name: "translate", defaultColor: BLUE },
  "start-audio": { name: "microphone", defaultColor: BRAND },
  "start-audio-red": { name: "microphone", defaultColor: RED },
  "audio-element-finished": { name: "speakerFill", defaultColor: GREEN },
  "audio-element-not-started": { name: "speakerFill", defaultColor: GRAY },
  "audio-element-ongoing-started": { name: "speakerFill", defaultColor: BRAND },
  "audio-element-started": { name: "speakerFill", defaultColor: BRAND },
};
